import {
    AwsFile,
    createAwsS3PresignedClient,
} from '../../aws/aws-s3-presigned-client';
import { FileUpload, Upload } from '@components/upload/Upload';
import { MAX_FILE_SIZE_BYTES } from '@utils/constants';
import { OrganizationDTO } from '@unternehmensverzeichnis/shared/types';
import { UploadStatus } from '@components/upload/UploadStatus';
import React, { useCallback, useEffect, useState } from 'react';

export const UploadCompanyLogoPresigned = ({
    value,
    setValue,
    updateCompanyLogo,
    initialValues,
    token,
}: {
    value?: string | null;
    setValue: (url: string | null) => void;
    updateCompanyLogo: (orgData: OrganizationDTO) => Promise<Response>;
    initialValues: OrganizationDTO;
    token: string;
}) => {
    const [currentFile, setCurrentFile] = useState<AwsFile | null>(null);
    const [fileUploads, setFileUploads] = useState<FileUpload<string>[]>(
        currentFile
            ? [
                  {
                      status: UploadStatus.PREEXISTING,
                      name: currentFile.name,
                      size: currentFile.size,
                      progress: 1,
                      id: 0,
                      remoteId: currentFile.id,
                      file: new File([], ''),
                  },
              ]
            : [],
    );

    // Create S3 presigned client with the token
    const s3Client = useCallback(() => {
        const client = createAwsS3PresignedClient();
        client.setToken(token);
        return client;
    }, [token]);

    useEffect(() => {
        if (value && token) {
            const fetchFile = async () => {
                const client = s3Client();
                const file = await client.getFile(value);

                if (file) {
                    setCurrentFile(file);
                    setFileUploads([
                        {
                            status: UploadStatus.PREEXISTING,
                            name: file.name,
                            size: file.size,
                            progress: 1,
                            id: 0,
                            remoteId: file.id,
                            file: new File([], ''),
                        },
                    ]);
                } else {
                    setFileUploads([]);
                }
            };
            void fetchFile();
        }
    }, [token, value]);

    const uploadFuncDb = useCallback(
        async (file: File, progressCallback: (progress: number) => void) => {
            const client = s3Client();
            const awsFile = await client.getUploadUrl(file);

            if (!awsFile) {
                return Promise.reject('Error uploading file');
            }

            progressCallback(1);
            setValue(awsFile.url);
            setCurrentFile(awsFile);

            await updateCompanyLogo({
                ...initialValues,
                companyLogoUrl: awsFile.url,
            });

            return Promise.resolve(awsFile.id);
        },
        [setValue, value, initialValues, token],
    );

    const deleteFunc = useCallback(
        async (awsFileId: string) => {
            const client = s3Client();
            const success = await client.deleteFile(awsFileId);

            if (success) {
                setValue(null);
                setCurrentFile(null);

                await updateCompanyLogo({
                    ...initialValues,
                    companyLogoUrl: undefined,
                });
            }
        },
        [setValue, initialValues, token],
    );

    return (
        <div>
            <h3 className="mb-1 font-normal">Firmenlogo</h3>
            {currentFile && (
                <img
                    src={currentFile.url}
                    alt="company logo"
                    className="max-h-[96px] mb-4 mx-4"
                />
            )}
            <Upload
                fileUploads={fileUploads}
                setFileUploads={setFileUploads}
                deleteFuncDB={deleteFunc}
                maxFileSize={MAX_FILE_SIZE_BYTES}
                maxFiles={1}
                uploadFuncDB={uploadFuncDb}
                acceptedFileFormats={{
                    'image/png': ['.png'],
                    'image/jpeg': ['.jpg', '.jpeg'],
                }}
            />
        </div>
    );
};
